// frontend/src/api/shareLinks.ts
/**
 * The owner's share links (logged in): contract section 5 of
 * docs/plans/2026-10-28-tank-shots-implementation.md. The public page does
 * not use this module (it must never go through apiClient, whose 401 sends
 * the visitor to /login): see api/publicShare.ts.
 */

import { api, apiClient } from './client';
import type {
  RevokeAllResponse,
  ShareLink,
  ShareLinkCreate,
  ShareLinkCreated,
  ShareLinkList,
} from '@/types/shareLinks';

/** An error the owner API answered with, keeping its status (409 is the per-tank cap). */
export class ShareLinkApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ShareLinkApiError';
    this.status = status;
  }
}

function linksPath(tankId: string): string {
  return `/api/tanks/${encodeURIComponent(tankId)}/share-links`;
}

/** FastAPI's detail when it is a sentence; a 422 carries a list, which says nothing to a person. */
async function errorOf(response: Response): Promise<ShareLinkApiError> {
  const body: unknown = await response.json().catch(() => null);
  const detail =
    body && typeof body === 'object' && 'detail' in body
      ? (body as { detail: unknown }).detail
      : null;
  const message = typeof detail === 'string' && detail ? detail : `HTTP ${response.status}`;
  return new ShareLinkApiError(response.status, message);
}

export const shareLinksApi = {
  /** The tank's live links, newest first. */
  async list(tankId: string): Promise<ShareLink[]> {
    const result = await api.get<ShareLinkList>(linksPath(tankId));
    return result.items;
  },

  /** 201: a new link. 200: the pair's existing live link (it keeps its own title). */
  async create(tankId: string, body: ShareLinkCreate): Promise<ShareLinkCreated> {
    const response = await apiClient(linksPath(tankId), {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!response.ok) throw await errorOf(response);
    const link = (await response.json()) as ShareLink;
    return { link, created: response.status === 201 };
  },

  /** Stop sharing one link (204, also when it was stopped already). */
  async revoke(tankId: string, linkId: number): Promise<void> {
    await api.delete(`${linksPath(tankId)}/${linkId}`);
  },

  /** Stop sharing every link on every tank of the account. */
  revokeAll(): Promise<RevokeAllResponse> {
    return api.post<RevokeAllResponse>('/api/share-links/revoke-all');
  },
};
